import { useCallback, useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Upload } from "lucide-react";

export default function AdminSpecialties() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);

    const load = useCallback(() => api.get("/admin/specialties").then(r => setItems(r.data)), []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form.name) return toast.error("Nome obrigatório");
        try {
            if (form.id) await api.put(`/admin/specialties/${form.id}`, form);
            else await api.post("/admin/specialties", form);
            toast.success("Guardado");
            setForm(null); load();
        } catch { toast.error("Erro"); }
    };

    const toggleActive = async (s) => {
        await api.put(`/admin/specialties/${s.id}`, { ...s, active: !s.active });
        load();
    };

    const move = async (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= items.length) return;
        const a = items[idx], b = items[target];
        await Promise.all([
            api.put(`/admin/specialties/${a.id}`, { ...a, order: b.order }),
            api.put(`/admin/specialties/${b.id}`, { ...b, order: a.order }),
        ]);
        load();
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar especialidade?")) return;
        await api.delete(`/admin/specialties/${id}`);
        load();
    };

    const uploadImg = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        return data.id;
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="font-display text-3xl font-bold text-stone-900">Especialidades</h1>
                <button onClick={() => setForm({ name: "", description: "", image_id: "", order: items.length + 1, active: true })}
                    data-testid="admin-specialty-add" className="btn-copper !py-2 !px-4 text-sm">
                    <Plus className="w-4 h-4" /> Nova especialidade
                </button>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
                {items.map((s, i) => (
                    <div key={s.id} className="p-3 flex items-center gap-3">
                        <div className="flex flex-col gap-0.5">
                            <button onClick={() => move(i, -1)} disabled={i === 0} className="text-stone-400 hover:text-stone-700 disabled:opacity-30">▲</button>
                            <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-stone-400 hover:text-stone-700 disabled:opacity-30">▼</button>
                        </div>
                        <GripVertical className="w-4 h-4 text-stone-300" />
                        {s.image_id && <img src={fileUrl(s.image_id)} alt="" className="w-10 h-10 rounded object-cover" />}
                        <div className="flex-1">
                            <div className="font-medium">{s.name}</div>
                            {s.description && <div className="text-xs text-stone-500 line-clamp-1">{s.description}</div>}
                        </div>
                        <label className="flex items-center gap-2 text-xs">
                            <input type="checkbox" checked={s.active} onChange={() => toggleActive(s)} className="accent-emerald-700" /> Ativa
                        </label>
                        <button onClick={() => setForm(s)} className="text-xs px-3 py-1 border border-stone-300 rounded">Editar</button>
                        <button onClick={() => del(s.id)} className="text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                ))}
                {items.length === 0 && <div className="p-10 text-center text-stone-500">Sem especialidades.</div>}
            </div>

            {form && (
                <div className="fixed inset-0 bg-stone-950/60 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
                    <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <h2 className="font-display font-bold text-xl">{form.id ? "Editar" : "Nova"} especialidade</h2>
                        <input placeholder="Nome" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border rounded p-2" />
                        <textarea placeholder="Descrição (opcional)" rows={3} value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full border rounded p-2" />
                        <div>
                            <label className="text-xs text-stone-500 block mb-1">Imagem (opcional)</label>
                            {form.image_id && <img src={fileUrl(form.image_id)} alt="" className="w-24 h-24 rounded object-cover mb-2" />}
                            <input type="file" accept="image/*" onChange={async (e) => { if (e.target.files[0]) { const id = await uploadImg(e.target.files[0]); setForm({...form, image_id: id}); }}} />
                        </div>
                        <input type="number" placeholder="Ordem" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} className="w-full border rounded p-2" />
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} /> Ativa</label>
                        <div className="flex gap-2 pt-2">
                            <button onClick={save} className="btn-copper flex-1 justify-center">Guardar</button>
                            <button onClick={() => setForm(null)} className="px-6 py-2 border border-stone-300 rounded-full">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
